import React from "react";
import { AppState, Auth0Provider } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";

type Props = { children: React.ReactNode };

const sanitizeReturnTo = (returnTo: unknown): string => {
  if (typeof returnTo !== "string") return "/admin";
  const trimmed = returnTo.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return "/admin";
  if (trimmed.includes("://")) return "/admin";
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("javascript:") || lower.startsWith("data:")) return "/admin";
  return trimmed;
};

export const Auth0ProviderWithHistory: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;

  if (!domain || !clientId) {
    throw new Error("Auth0 domain and client ID must be set in .env");
  }

  const onRedirectCallback = (appState?: AppState) => {
    const target = sanitizeReturnTo(appState?.returnTo);

    // Avoid navigating inside Auth0's silent-auth iframe
    try {
      const inIframe = window.self !== window.top;
      if (inIframe) return;
    } catch {
      return;
    }

    try {
      sessionStorage.setItem("AUTH_POST_CALLBACK_TS", String(Date.now()));
    } catch {
      // Ignore sessionStorage errors (e.g., private browsing mode)
    }

    // Use router navigation so React Router updates location without reload
    navigate(target, { replace: true });
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: import.meta.env.VITE_AUTH0_SCOPE || "openid profile email",
      }}
      // Use localStorage to persist tokens across page reloads.
      // This is critical for hard navigations (window.location.href) like after
      // onboarding completion or returning from external payment providers.
      cacheLocation="localstorage"
      // Use refresh tokens instead of hidden-iframe silent auth (/authorize?prompt=none).
      // Without this, Auth0 periodically opens hidden iframes to get fresh tokens, which
      // triggers Auth0Provider state changes → cascading re-renders → full component tree
      // re-initialization (new service instances, API refetches, SignalR reconnects).
      // Refresh tokens use a direct /oauth/token API call — no iframes, no state changes.
      // Requires "Refresh Token Rotation" enabled in Auth0 Dashboard.
      useRefreshTokens={true}
      // Don't fall back to iframe-based silent auth if refresh token exchange fails.
      // Modern browsers block third-party cookies needed for iframes. If refresh fails,
      // the user simply re-authenticates — cleaner than a broken iframe flow.
      useRefreshTokensFallback={false}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory;
